/**
 * Single wallet abstraction (wagmi/viem).
 * Coinbase Wallet + MetaMask (injected) + WalletConnect (conditional).
 */
import { http, createConfig, type CreateConnectorFn } from "wagmi";
import { coinbaseWallet, injected, metaMask, walletConnect } from "wagmi/connectors";
import type { Chain } from "viem";
import { getConfiguredChainId, selectChain } from "./chain";

const enableWc = String(import.meta.env.ENABLE_WALLETCONNECT || "false").toLowerCase() === "true";
const wcProjectId = String(import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || "").trim();

if (enableWc && !wcProjectId) {
  throw new Error(
    "ENABLE_WALLETCONNECT=true but VITE_WALLETCONNECT_PROJECT_ID is empty",
  );
}

const chainId = getConfiguredChainId();
const chain: Chain = selectChain(chainId);
const rpcUrl = String(import.meta.env.VITE_BASE_RPC_URL || "");

// MACRO #31 mobile hotfix — the official MetaMask connector (MetaMask SDK, a
// direct dependency of @wagmi/connectors; no extra package, no WalletConnect
// project id). It gives a REAL MetaMask option on mobile Safari/Chrome that
// deep-links to the MetaMask app, uses the extension on desktop, and the
// injected provider inside the MetaMask in-app browser.
const appUrl = typeof window !== "undefined" ? window.location.origin : "https://thegrid.app";

const connectors: CreateConnectorFn[] = [
  metaMask({ dappMetadata: { name: "THE GRID", url: appUrl } }) as unknown as CreateConnectorFn,
  coinbaseWallet({ appName: "THE GRID" }) as unknown as CreateConnectorFn,
  injected({ shimDisconnect: true }) as unknown as CreateConnectorFn,
];
if (enableWc && wcProjectId) {
  connectors.push(
    walletConnect({ projectId: wcProjectId, showQrModal: true }) as unknown as CreateConnectorFn,
  );
}

export const wagmiConfig = createConfig({
  chains: [chain],
  connectors,
  transports: {
    [chain.id]: http(rpcUrl || undefined),
  },
});

export { chain, chainId };
